import * as React from 'react';
import type {
  EnvironmentKey,
  DomainEntity,
  ConfigEntityDetail,
} from '@web-config-app/core';
import {
  useBulkDeepPublish,
  useGetEntityTypeName,
} from '@web-config-app/core-react';
import type { BulkActionReportingStore } from '@web-config-app/core-react';
import {
  BulkDeepPublishConfirmationModal,
  type BulkPublishRootNode,
  type DeepPublishTreeNode,
} from '@web-config-app/core-react-ui';
import { useGetEntityDetailPath } from '../../hooks/use-get-entity-detail-path/use-get-entity-detail-path';
import { buildTreeNode } from '../deep-publish-modal-container/deep-publish-modal.container';

interface BulkDeepPublishModalContainerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  domainEntity: DomainEntity;
  environment: EnvironmentKey;
  selectedEntities: ConfigEntityDetail[];
  bulkActionReportingStore: BulkActionReportingStore;
  onBulkPublishConfirm: () => void;
  /** Mutable ref populated with executeBulkDeepPublish so the parent can trigger a retry */
  retryPublishRef?: React.MutableRefObject<(() => void) | undefined>;
  /** Mutable ref populated with cancelPublish so the parent can cancel the run */
  cancelPublishRef?: React.MutableRefObject<(() => void) | undefined>;
  /** Mutable ref populated with reset so the parent can clear state (e.g. on Done) */
  resetRef?: React.MutableRefObject<(() => void) | undefined>;
}

export const BulkDeepPublishModalContainer = ({
  isOpen,
  setIsOpen,
  domainEntity,
  environment,
  selectedEntities,
  bulkActionReportingStore,
  onBulkPublishConfirm,
  retryPublishRef,
  cancelPublishRef,
  resetRef,
}: BulkDeepPublishModalContainerProps) => {
  const getEntityTypeName = useGetEntityTypeName();
  const getEntityDetailPath = useGetEntityDetailPath();
  const getEntityDetailPathForEnvironment = React.useCallback(
    (entityType: string, instanceId: string) =>
      getEntityDetailPath(entityType, instanceId, environment),
    [environment, getEntityDetailPath],
  );

  const bulkPublish = useBulkDeepPublish({
    domainEntity,
    environment,
    bulkActionReportingStore,
    getEntityDetailPath: getEntityDetailPathForEnvironment,
  });

  // Discover trees when modal opens
  React.useEffect(() => {
    if (isOpen && selectedEntities.length > 0) {
      bulkPublish.discoverTrees(selectedEntities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- discoverTrees excluded intentionally: we only want to trigger discovery when the modal opens or selection changes, not when the callback identity changes
  }, [isOpen, selectedEntities]);

  const translateNodeTypes = React.useCallback(
    (node: DeepPublishTreeNode): DeepPublishTreeNode => ({
      ...node,
      domainEntityType: getEntityTypeName(node.domainEntityType, 1),
      childNodes: node.childNodes.map(translateNodeTypes),
    }),
    [getEntityTypeName],
  );

  const rootNodes: BulkPublishRootNode[] = React.useMemo(
    () =>
      bulkPublish.rootTrees.map((rootTree) => {
        const treeNode = buildTreeNode(
          rootTree.rootEntity.id,
          rootTree.treeEntries,
          rootTree.discoveryFailures,
        );

        const translatedNode = treeNode
          ? translateNodeTypes(treeNode)
          : undefined;

        const rootMetadata = rootTree.rootEntity.attributes?.entityMetadata;
        const rootEntityName = rootMetadata?.name ?? rootTree.rootEntity.id;
        const rootDomainEntityType = getEntityTypeName(
          rootMetadata?.domainEntityType ?? '',
          1,
        );
        const rootStatus = rootMetadata?.status ?? 'draft';

        const errorCount =
          rootTree.discoveryFailures.length +
          (translatedNode?.childNodes.filter((child) => child.error).length ??
            0);

        return {
          rootEntityId: rootTree.rootEntity.id,
          rootEntityName,
          rootDomainEntityType,
          rootStatus,
          rootLink: getEntityDetailPathForEnvironment(
            rootMetadata?.domainEntityType ?? '',
            rootTree.rootEntity.id,
          ),
          treeNode: translatedNode ?? {
            id: rootTree.rootEntity.id,
            entityName: rootEntityName,
            domainEntityType: rootDomainEntityType,
            status: rootStatus,
            childNodes: [],
          },
          errorCount,
        };
      }),
    [
      bulkPublish.rootTrees,
      translateNodeTypes,
      getEntityTypeName,
      getEntityDetailPathForEnvironment,
    ],
  );

  const handleConfirm = React.useCallback(() => {
    setIsOpen(false);
    onBulkPublishConfirm();
    bulkPublish.executeBulkDeepPublish();
  }, [setIsOpen, onBulkPublishConfirm, bulkPublish]);

  // Expose callbacks to the parent via refs
  React.useEffect(() => {
    if (retryPublishRef) {
      // eslint-disable-next-line no-param-reassign -- intentional: populating parent-owned mutable ref
      retryPublishRef.current = bulkPublish.executeBulkDeepPublish;
    }
  }, [retryPublishRef, bulkPublish.executeBulkDeepPublish]);

  React.useEffect(() => {
    if (cancelPublishRef) {
      // eslint-disable-next-line no-param-reassign -- intentional: populating parent-owned mutable ref
      cancelPublishRef.current = bulkPublish.cancelPublish;
    }
  }, [cancelPublishRef, bulkPublish.cancelPublish]);

  React.useEffect(() => {
    if (resetRef) {
      // eslint-disable-next-line no-param-reassign -- intentional: populating parent-owned mutable ref
      resetRef.current = bulkPublish.reset;
    }
  }, [resetRef, bulkPublish.reset]);

  const handleClose = React.useCallback(() => {
    setIsOpen(false);
    bulkPublish.reset();
  }, [setIsOpen, bulkPublish]);

  return (
    <BulkDeepPublishConfirmationModal
      isOpen={isOpen}
      isLoading={bulkPublish.isDiscovering}
      rootNodes={rootNodes}
      errorMessage={bulkPublish.discoveryErrorMessage}
      hasFailures={bulkPublish.hasDiscoveryFailures}
      totalDraftCount={bulkPublish.totalDraftCount}
      discoveredSoFar={bulkPublish.discoveredSoFar}
      onConfirm={handleConfirm}
      onClose={handleClose}
    />
  );
};
